import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Shield, Cloud, Users, Settings, Clock, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const features = [
  {
    icon: Mail,
    title: 'Fagleg netföng',
    description: 'Sérsniðin netföng með þínu léni.'
  },
  {
    icon: Shield,
    title: 'Örugg þjónusta',
    description: 'Öflug vörn gegn ruslpósti og vírusum.'
  },
  {
    icon: Cloud,
    title: 'Stór pósthólf',
    description: 'Nægt geymslupláss fyrir öll þín gögn.'
  },
  {
    icon: Users,
    title: 'Samvinna',
    description: 'Deiling á dagbókum og tengiliðum.'
  },
  {
    icon: Settings,
    title: 'Sérsniðin virkni',
    description: 'Aðlagað að þínum þörfum.'
  },
  {
    icon: Clock,
    title: '24/7 þjónusta',
    description: 'Stöðug vöktun og aðstoð.'
  }
];

const packages = [
  {
    name: 'Basic',
    price: '990 kr./mán',
    description: 'Fyrir einstaklinga',
    features: [
      '10 GB pósthólf',
      'Vörn gegn ruslpósti',
      'Vírusvörn',
      'Vefpóstur',
      'IMAP/POP3 aðgangur',
      'SSL dulkóðun'
    ]
  },
  {
    name: 'Business',
    price: '1.990 kr./mán',
    description: 'Fyrir lítil fyrirtæki',
    features: [
      '50 GB pósthólf',
      'Vörn gegn ruslpósti',
      'Vírusvörn',
      'Vefpóstur',
      'IMAP/POP3 aðgangur',
      'SSL dulkóðun',
      'Sameiginleg dagbók',
      'Tengiliðastjórnun',
      'Forgangsþjónusta'
    ],
    popular: true
  },
  {
    name: 'Enterprise',
    price: '3.990 kr./mán',
    description: 'Fyrir stærri fyrirtæki',
    features: [
      '100 GB pósthólf',
      'Vörn gegn ruslpósti',
      'Vírusvörn',
      'Vefpóstur',
      'IMAP/POP3 aðgangur',
      'SSL dulkóðun',
      'Sameiginleg dagbók',
      'Tengiliðastjórnun',
      'Forgangsþjónusta',
      'Sérsniðin þjónusta',
      'Öryggisafritun',
      'VIP stuðningur'
    ]
  }
];

const Email = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Örugg og áreiðanleg tölvupóstþjónusta
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Fagleg netföng með þínu léni og öflug tölvupóstþjónusta fyrir 
              einstaklinga og fyrirtæki.
            </p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center"
                >
                  Fá tilboð í póstþjónustu
                  <ArrowRight className="ml-2" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Tölvupóstþjónusta í sérflokki
            </h2>
            <p className="text-xl text-gray-600">
              Við bjóðum upp á heildstæða tölvupóstþjónustu
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-gray-50 rounded-xl p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  <feature.icon className="w-6 h-6 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Packages Section */}
      <motion.div 
        className="py-24 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Póstþjónustupakkar
            </h2>
            <p className="text-xl text-gray-600">
              Veldu þann pakka sem hentar þínum þörfum
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <motion.div 
                key={index}
                className={`bg-white rounded-xl p-8 shadow-sm ${
                  pkg.popular ? 'border-2 border-[#EA295A]' : ''
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                {pkg.popular && (
                  <div className="bg-[#EA295A]/10 text-[#EA295A] text-sm font-medium px-3 py-1 rounded-full inline-block mb-4">
                    Vinsælast
                  </div>
                )}
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                <div className="text-3xl font-bold text-[#EA295A] mb-1">{pkg.price}</div>
                <p className="text-gray-500 text-sm mb-4">verð án VSK</p>
                <p className="text-gray-600 mb-6">{pkg.description}</p>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-center text-gray-600">
                      <CheckCircle2 className="w-5 h-5 text-[#EA295A] mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/fa-tilbod">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full px-6 py-3 rounded-lg font-medium flex items-center justify-center ${
                      pkg.popular
                        ? 'bg-[#EA295A] text-white hover:bg-[#d01d4c]'
                        : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                    } transition-all`}
                  >
                    Velja pakka
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </motion.button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
            <motion.h2 
              className="text-3xl font-bold text-white mb-6"
            >
              Tilbúin að hefja samstarf?
            </motion.h2>
            <motion.p 
              className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            >
              Láttu okkur hjálpa þér að finna réttu póstþjónustuna fyrir þitt 
              fyrirtæki
            </motion.p>
            <motion.div>
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                >
                  Fá tilboð í póstþjónustu
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Email;